/**
 * Admin → Algemene mailboxen → info@ (recht admin_settings).
 * Schakelaars (env = plafond), per categorie negeren / samenvatting / melden / archiveren, altijd-negeren-lijst,
 * PO-overzicht met "Zet in Gripp" (bevestig) en "Klopt niet" (ongedaan), de link "PO-bijlage" (pdf, korte
 * leeslink na de rechtencheck; ook vanuit de rapportmail via ?po_bijlage=) en de terugkijkanalyse.
 * Rustig: neutrale tinten, één accent, de terugkijkanalyse standaard ingeklapt.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  Alert, Box, Button, Checkbox, CircularProgress, FormControlLabel, MenuItem, Table, TableBody, TableCell, TableHead,
  TableRow, TextField, Typography,
} from '@mui/material';
import toast from 'react-hot-toast';
import { bevestig } from '../../../utils/bevestig';
import { biedOngedaanAan, registreerOngedaanSneltoets } from '../../../utils/ongedaan';
import Schakelaar from './Schakelaar';
import { adressen, mailboxApi } from './api';

const GRIJS = '#556071';
const RAND = '#e5e9eb';

export const UITKOMST_LABEL = {
  ingevuld: 'Automatisch in Gripp gezet', zou_invullen: 'Kan in Gripp (veld is leeg)', al_gelijk: 'Staat al goed',
  afwijking: 'Gripp heeft een andere referentie', niet_eenduidig: 'Niet eenduidig — handmatig koppelen',
  geen_match: 'Geen project gevonden', schrijffout: 'Schrijven mislukt', geblokkeerd: 'Geblokkeerd (omgeving)',
  wacht_gripp: 'Wacht op Gripp', al_ingevuld: 'Al ingevuld — geen actie nodig',
};
const ACTIE_LABEL = { gripp_gezet: 'In Gripp gezet', klopt_niet: 'Klopt niet', al_gelijk: 'Stond al goed', afwijking: 'Gripp had al iets anders' };
const ACTIES = [
  ['negeren', 'Negeren'], ['samenvatting', 'In de dagelijkse samenvatting'], ['melden', 'Melden aan…'],
  ['archiveren', 'Archiveren (naar "Archief (tool)")'],
];
/** Archiveren kan alleen voor de categorieën die de engine archiveerbaar noemt (nooit sollicitatie, factuur, aanvraag). */
export const actiesVoor = (groep) => ACTIES.filter(([w]) => w !== 'archiveren' || groep?.archiveerbaar);
const SCHAKELAAR_TITEL = {
  ALGMAIL_PO_CHECK: 'PO-check', ALGMAIL_PO_GRIPP_WRITE: 'Gripp-schrijven', ALGMAIL_PO_MATCH_MAIL: 'Mail bij een PO-match',
  ALGMAIL_INFO_MELDINGEN: 'Meldingen en samenvatting', ALGMAIL_INFO_ARCHIVEREN: 'Archiveren',
};

const Kop = ({ children, sub }) => (
  <Box sx={{ mt: 5, mb: 1.5 }}>
    <Typography sx={{ fontWeight: 700, fontSize: '1rem' }}>{children}</Typography>
    {sub && <Typography sx={{ fontSize: '0.8rem', color: GRIJS }}>{sub}</Typography>}
  </Box>
);

const datum = (s) => (s ? new Date(s).toLocaleDateString('nl-NL', { day: 'numeric', month: 'short', year: '2-digit' }) : '');

/** De PO-pdf openen: de engine geeft na de rechtencheck een korte leeslink (15 min); de pdf zit nooit in een mail. */
export async function openPoBijlage(sleutel, nr, venster = null) {
  try {
    const { url } = await mailboxApi.poBijlage(sleutel, nr);
    if (venster && !venster.closed) venster.location.href = url;
    else window.location.assign(url);
  } catch (e) {
    if (venster && !venster.closed) venster.close();
    toast.error(e.message);
  }
}

/** Link uit de rapportmail: ?po_bijlage=<sleutel>.<nr> → meteen openen, en de parameter uit de adresbalk halen. */
export function poBijlageUitAdres(search = window.location.search) {
  const waarde = new URLSearchParams(search).get('po_bijlage') || '';
  const m = waarde.match(/^([0-9a-f]{64})\.(\d)$/);
  return m ? { sleutel: m[1], nr: Number(m[2]) } : null;
}

export function PoBijlagen({ sleutel, bijlagen }) {
  if (!sleutel || !bijlagen?.length) return null;
  return bijlagen.map((b, i) => (
    <Button key={b.nr} size="small" title={b.naam}
      onClick={() => {
        const venster = window.open('', '_blank');
        if (venster) venster.opener = null;
        openPoBijlage(sleutel, b.nr, venster);
      }}
      sx={{ textTransform: 'none', minWidth: 0, p: 0, fontSize: '0.74rem', display: 'block' }}>
      {bijlagen.length > 1 ? `PO-bijlage ${i + 1}` : 'PO-bijlage'} (pdf)
    </Button>
  ));
}

export function PoRegels({ regels, mag, onZet, onKloptNiet, onTrekIn, bron }) {
  if (!regels.length) return <Typography sx={{ fontSize: '0.86rem', color: GRIJS, py: 1 }}>Nog geen PO's gevonden.</Typography>;
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ '& td, & th': { borderColor: RAND, fontSize: '0.82rem', py: 1 } }}>
        <TableHead>
          <TableRow>
            {['Ontvangen', 'PO', 'Project', 'Uitkomst', 'Gripp nu', ''].map((k) => (
              <TableCell key={k} sx={{ color: GRIJS, fontWeight: 600 }}>{k}</TableCell>))}
          </TableRow>
        </TableHead>
        <TableBody>
          {regels.map((r, i) => {
            const project = typeof r.project === 'string' ? r.project
              : [r.project?.nummer, r.project?.naam].filter(Boolean).join(' ');
            const uitkomst = bron === 'terugkijk' ? r.zou_doen : r.uitkomst;
            const gripp = bron === 'terugkijk' ? (r.gripp_vergelijking || '—') : (r.gripp || '—');
            const voorstel = project ? [] : (r.kandidaten || []).filter((k) => k && k.gripp_id);
            const zonderId = project ? [] : (r.kandidaten || []).filter((k) => k && !k.gripp_id && k.label);
            const al = (r.staat_al_in_gripp || [])[0];
            return (
              <TableRow key={`${r.sleutel || r.po}-${r.gripp_id || r.project?.gripp_id || i}`}>
                <TableCell sx={{ whiteSpace: 'nowrap', color: GRIJS }}>{datum(r.datum)}{r.modus === 'dry' ? ' · proef' : ''}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>
                  {r.po}
                  {bron !== 'terugkijk' && <PoBijlagen sleutel={r.sleutel} bijlagen={r.bijlagen} />}
                </TableCell>
                <TableCell>
                  {project || (!voorstel.length && (zonderId.map((k) => k.label).join('; ') || '—'))}
                  {voorstel.map((k) => (
                    <Box key={k.gripp_id} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography component="span" sx={{ fontSize: '0.82rem' }}>
                        {k.label || `${k.nummer} ${k.naam}`}
                        {(k.status === 3 || k.afgerond) && <Typography component="span" sx={{ fontSize: '0.74rem', color: GRIJS }}> · project al afgerond</Typography>}
                      </Typography>
                      {k.handmatig ? (
                        <Typography component="span" sx={{ fontSize: '0.74rem', color: GRIJS }}>{ACTIE_LABEL[k.handmatig.actie] || k.handmatig.actie}</Typography>
                      ) : k.kan_zetten && !r.handmatig && (
                        <Button size="small" disabled={!mag} onClick={() => onZet(r, k)} sx={{ textTransform: 'none', minWidth: 0, py: 0 }}>
                          Zet in Gripp
                        </Button>
                      )}
                    </Box>
                  ))}
                  {r.afzender_domein && <Typography sx={{ fontSize: '0.74rem', color: GRIJS }}>{r.afzender_domein}</Typography>}
                </TableCell>
                <TableCell>
                  {r.uitkomst_label || UITKOMST_LABEL[uitkomst] || uitkomst}
                  {['failed', 'fout'].includes(r.match_mail) && (
                    <Typography component="span" sx={{ display: 'block', fontSize: '0.74rem', color: '#b42318' }}>
                      Match-mail niet verstuurd
                    </Typography>
                  )}
                  {r.reden && <Typography sx={{ fontSize: '0.74rem', color: GRIJS }}>{r.reden}</Typography>}
                  {r.bron && <Typography sx={{ fontSize: '0.74rem', color: GRIJS }}>{r.bron}</Typography>}
                  {al && (
                    <Typography sx={{ fontSize: '0.74rem', color: GRIJS }}>
                      Staat al in Gripp op {al.soort === 'offerte' ? 'offerte' : 'project'} {al.nummer} (gelijk)
                    </Typography>
                  )}
                </TableCell>
                <TableCell sx={{ color: GRIJS }}>{gripp}</TableCell>
                <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'right' }}>
                  {r.handmatig ? (
                    <Typography component="span" sx={{ fontSize: '0.78rem', color: GRIJS }}>
                      {ACTIE_LABEL[r.handmatig.actie] || r.handmatig.actie} · {r.handmatig.door?.split('@')[0]}
                      {r.handmatig.actie === 'klopt_niet' && (
                        <Button size="small" onClick={() => onTrekIn(r.handmatig)} sx={{ textTransform: 'none', ml: 1, minWidth: 0 }}>Herstel</Button>
                      )}
                    </Typography>
                  ) : (
                    <>
                      {r.kan_zetten && (
                        <Button size="small" variant="outlined" disabled={!mag} onClick={() => onZet(r)}
                          sx={{ textTransform: 'none', mr: 1 }}>Zet in Gripp</Button>
                      )}
                      <Button size="small" onClick={() => onKloptNiet(r)} sx={{ textTransform: 'none', color: GRIJS }}>Klopt niet</Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </Box>
  );
}

export default function InfoMailbox() {
  const [inst, setInst] = useState(null);
  const [vorm, setVorm] = useState(null);
  const [bezig, setBezig] = useState(false);
  const [po, setPo] = useState(null);
  const [terug, setTerug] = useState(null);
  const [terugStatus, setTerugStatus] = useState(null);
  const [terugOpen, setTerugOpen] = useState(false);

  useEffect(() => registreerOngedaanSneltoets(), []);

  // Link 'PO-bijlage' uit de rapportmail: na het laden (rechtencheck in de engine) meteen de pdf openen.
  useEffect(() => {
    const doel = poBijlageUitAdres();
    if (!doel) return;
    const sp = new URLSearchParams(window.location.search);
    sp.delete('po_bijlage');
    window.history.replaceState({}, '', `${window.location.pathname}?${sp.toString()}`);
    openPoBijlage(doel.sleutel, doel.nr);
  }, []);

  const laadInst = useCallback(async () => {
    try {
      const d = await mailboxApi.instellingen();
      setInst(d);
      setVorm({
        categorieen: d.info.categorieen,
        negeren: (d.info.negeren || []).join('\n'),
        overdracht: (d.info.overdracht || []).join('\n'),
        account: (d.info.account || []).join('\n'),
        samenvatting: (d.info.samenvatting_ontvangers || []).join(', '),
        po_ontvangers: (d.po_ontvangers || []).join(', '),
        po_match_ontvangers: (d.po_match_ontvangers || []).join(', '),
        uitleg_aan: d.info.samenvatting_uitleg?.aan !== false,
        uitleg_tekst: d.info.samenvatting_uitleg?.tekst || '',
      });
    } catch (e) { toast.error(e.message); }
  }, []);
  const laadPo = useCallback(async () => {
    try { setPo(await mailboxApi.poOverzicht()); } catch (e) { toast.error(e.message); setPo({ regels: [] }); }
  }, []);
  const laadTerug = useCallback(async () => {
    try {
      const [st, laatste] = await Promise.all([mailboxApi.terugkijkStatus(), mailboxApi.terugkijkLaatste()]);
      setTerugStatus(st); setTerug(laatste);
    } catch (e) { toast.error(e.message); }
  }, []);
  useEffect(() => { laadInst(); laadPo(); laadTerug(); }, [laadInst, laadPo, laadTerug]);

  useEffect(() => {
    if (!terugStatus?.bezig) return undefined;
    const t = setTimeout(laadTerug, 10000);
    return () => clearTimeout(t);
  }, [terugStatus, laadTerug]);

  const zetSchakelaar = async (naam, niveau) => { await mailboxApi.zetSchakelaar(naam, niveau); await laadInst(); await laadPo(); };

  const opslaan = async () => {
    setBezig(true);
    try {
      await mailboxApi.zetInfo({
        categorieen: vorm.categorieen, negeren: adressen(vorm.negeren), overdracht: adressen(vorm.overdracht),
        account: adressen(vorm.account),
        samenvatting_ontvangers: adressen(vorm.samenvatting),
        samenvatting_uitleg: { aan: vorm.uitleg_aan, tekst: vorm.uitleg_tekst },
      });
      await mailboxApi.zetPoOntvangers(adressen(vorm.po_ontvangers), adressen(vorm.po_match_ontvangers));
      toast.success('Opgeslagen');
      await laadInst();
    } catch (e) { toast.error(e.message); } finally { setBezig(false); }
  };

  const sleutelVan = (r, bron, k) => {
    const gid = k?.gripp_id ?? (bron === 'terugkijk' ? r.project?.gripp_id : r.gripp_id) ?? null;
    return bron === 'terugkijk'
      ? { bron, run_id: terug?.id, po: r.po, gripp_id: gid }
      : { bron, sleutel: r.sleutel, po: r.po, gripp_id: gid };
  };

  const zetInGripp = async (r, bron, k) => {
    const project = k ? (k.label || `${k.nummer} ${k.naam}`)
      : (typeof r.project === 'string' ? r.project : `${r.project?.nummer} ${r.project?.naam}`);
    const ok = await bevestig({
      titel: 'PO in Gripp zetten?',
      tekst: `PO ${r.po} wordt in Gripp als klantreferentie op project ${project} gezet. Alleen als dat veld nu leeg is; `
        + 'het wordt vlak ervoor en erna opnieuw gelezen en nooit overschreven.',
      knop: 'Zet in Gripp',
    });
    if (!ok) return;
    try {
      const uit = await mailboxApi.zetInGripp(sleutelVan(r, bron, k));
      if (uit.actie === 'gripp_gezet') toast.success(`PO ${r.po} staat nu in Gripp`);
      else toast(`Niet gezet: ${UITKOMST_LABEL[uit.actie] || uit.actie}${uit.gripp_waarde ? ` (${uit.gripp_waarde})` : ''}`);
    } catch (e) { toast.error(e.message); }
    await (bron === 'terugkijk' ? laadTerug() : laadPo());
  };

  const kloptNiet = async (r, bron) => {
    try {
      const uit = await mailboxApi.kloptNiet(sleutelVan(r, bron));
      const herlaad = bron === 'terugkijk' ? laadTerug : laadPo;
      await herlaad();
      biedOngedaanAan(`PO ${r.po}: klopt niet`, async () => { await mailboxApi.trekIn(uit.audit_id); await herlaad(); });
    } catch (e) { toast.error(e.message); }
  };

  const trekIn = async (a, bron) => {
    try { await mailboxApi.trekIn(a.id); await (bron === 'terugkijk' ? laadTerug() : laadPo()); } catch (e) { toast.error(e.message); }
  };

  const startTerug = async (maanden) => {
    const ok = await bevestig({
      titel: `info@ ${maanden} maanden terugkijken?`,
      tekst: 'Leest info@ (alleen lezen) en laat de PO-herkenning (AI) lopen op mails met een PO-signaal. Er wordt niets '
        + 'naar Gripp geschreven en niets gemaild. Duurt enkele minuten.',
      knop: 'Start',
    });
    if (!ok) return;
    try { await mailboxApi.terugkijkStart(maanden); await laadTerug(); setTerugOpen(true); } catch (e) { toast.error(e.message); }
  };

  if (!inst || !vorm) return <CircularProgress size={20} sx={{ color: '#adb3b5' }} />;
  const rij = (n) => inst.schakelaars.find((s) => s.naam === n);
  const mag = inst.handmatig_schrijven?.toegestaan;
  const res = terug?.resultaat;

  return (
    <Box sx={{ maxWidth: 1100 }}>
      {!inst.geconfigureerd && (
        <Alert severity="info" sx={{ mb: 2 }}>De mailbox-koppeling (MS_GRAPH_ALGMAIL_*) is in deze omgeving nog niet ingesteld.</Alert>
      )}
      <Typography sx={{ fontSize: '0.84rem', color: GRIJS, mb: 1 }}>
        info@ wordt alleen gelezen. Er gaat nooit iets naar buiten: meldingen en rapporten gaan alleen naar interne adressen.
      </Typography>

      <Kop sub="De omgeving is het plafond: wat je hier kiest, werkt alleen tot die stand.">Schakelaars</Kop>
      {['ALGMAIL_PO_CHECK', 'ALGMAIL_PO_GRIPP_WRITE', 'ALGMAIL_PO_MATCH_MAIL', 'ALGMAIL_INFO_MELDINGEN', 'ALGMAIL_INFO_ARCHIVEREN'].filter(rij).map((n) => (
        <Box key={n} sx={{ borderTop: `1px solid ${RAND}` }}>
          <Schakelaar rij={rij(n)} titel={SCHAKELAAR_TITEL[n]} onZet={(niveau) => zetSchakelaar(n, niveau)} />
        </Box>
      ))}
      {inst.info.archief?.geen_recht_sinds && (
        <Typography sx={{ fontSize: '0.78rem', color: '#92400e', mt: 0.5 }}>
          Archiveren lukt niet: de koppeling mag in info@ nog niet verplaatsen (Mail.ReadWrite ontbreekt). De tool
          probeert het na een pauze opnieuw; de mails blijven gewoon in de inbox.
        </Typography>
      )}

      <Kop sub={`Indeling op onderwerp en afzender, zonder AI. Archiveren verplaatst naar de map "${inst.info.archief_map || 'Archief (tool)'}" (nooit verwijderen) en werkt pas als de schakelaar Archiveren aan staat; tot die tijd telt het als negeren.`}>Per categorie</Kop>
      {inst.info.groepen.map((g) => {
        const c = vorm.categorieen[g.sleutel] || { actie: 'samenvatting', ontvangers: [] };
        const zet = (nieuw) => setVorm({ ...vorm, categorieen: { ...vorm.categorieen, [g.sleutel]: { ...c, ...nieuw } } });
        return (
          <Box key={g.sleutel} sx={{ display: 'flex', alignItems: 'center', gap: 2, py: 1, borderTop: `1px solid ${RAND}`, flexWrap: 'wrap' }}>
            <Typography sx={{ width: 200, fontSize: '0.88rem' }}>{g.label}</Typography>
            <TextField select size="small" value={c.actie} onChange={(e) => zet({ actie: e.target.value })} sx={{ width: 260 }}>
              {actiesVoor(g).map(([w, l]) => <MenuItem key={w} value={w}>{l}</MenuItem>)}
            </TextField>
            {c.actie === 'melden' && (
              <TextField size="small" placeholder="naam@fullframe.nl, …" sx={{ flex: 1, minWidth: 240 }}
                value={(c.ontvangers || []).join(', ')} onChange={(e) => zet({ ontvangers: adressen(e.target.value) })} />
            )}
          </Box>
        );
      })}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 3, mt: 3 }}>
        <Box sx={{ display: 'grid', gap: 2, alignContent: 'start' }}>
          <TextField label="Altijd negeren (domein of adres, één per regel)" multiline minRows={4}
            value={vorm.negeren} onChange={(e) => setVorm({ ...vorm, negeren: e.target.value })} />
          <TextField label="Overdrachtsdiensten (domein of adres, één per regel)" multiline minRows={4}
            helperText="Filemail, WeTransfer e.d. worden 'Aangeleverd materiaal' en nooit gearchiveerd. Ook herkend op onderwerp, zoals 'Received files'."
            value={vorm.overdracht} onChange={(e) => setVorm({ ...vorm, overdracht: e.target.value })} />
          <TextField label="Account- en beveiligingsdiensten (domein of adres, één per regel)" multiline minRows={4}
            helperText="Factuur, opwaardering of proefperiode van deze diensten wordt 'Account & beveiliging' en nooit gearchiveerd. Inlogmeldingen, wachtwoordresets en codes worden altijd herkend; nieuwsbrieven blijven nieuwsbrief."
            value={vorm.account} onChange={(e) => setVorm({ ...vorm, account: e.target.value })} />
        </Box>
        <Box sx={{ display: 'grid', gap: 2, alignContent: 'start' }}>
          <TextField size="small" label="Dagelijkse samenvatting naar" value={vorm.samenvatting}
            onChange={(e) => setVorm({ ...vorm, samenvatting: e.target.value })} />
          <FormControlLabel sx={{ mt: -1 }}
            control={<Checkbox size="small" checked={!!vorm.uitleg_aan} onChange={(e) => setVorm({ ...vorm, uitleg_aan: e.target.checked })} />}
            label={<Typography sx={{ fontSize: '0.84rem' }}>Uitleg bovenaan de samenvatting</Typography>} />
          {vorm.uitleg_aan && (
            <TextField size="small" multiline minRows={2} label="Uitleg (leeg = standaardtekst)"
              placeholder={inst.info.standaard_uitleg || ''} value={vorm.uitleg_tekst} inputProps={{ maxLength: 600 }}
              onChange={(e) => setVorm({ ...vorm, uitleg_tekst: e.target.value })} />
          )}
          <TextField size="small" label="PO-rapport naar" value={vorm.po_ontvangers}
            onChange={(e) => setVorm({ ...vorm, po_ontvangers: e.target.value })} />
          <TextField size="small" label="Mail bij een PO-match naar (de PL van het project komt erbij)"
            value={vorm.po_match_ontvangers} helperText="Alleen @fullframe.nl"
            onChange={(e) => setVorm({ ...vorm, po_match_ontvangers: e.target.value })} />
        </Box>
      </Box>
      <Button variant="contained" disableElevation onClick={opslaan} disabled={bezig}
        sx={{ mt: 2, textTransform: 'none', fontWeight: 700 }}>
        {bezig ? <CircularProgress size={16} color="inherit" /> : 'Opslaan'}
      </Button>

      <Kop sub="Gevonden PO's uit info@ en wat ermee gebeurd is. 'Zet in Gripp' schrijft alleen als het veld leeg is.">PO-overzicht</Kop>
      {!mag && (
        <Typography sx={{ fontSize: '0.78rem', color: GRIJS, mb: 1 }}>
          Zet in Gripp kan nu niet: {(inst.handmatig_schrijven?.redenen || []).join('; ')}.
        </Typography>
      )}
      {po ? (
        <PoRegels regels={po.regels || []} mag={mag} bron="live"
          onZet={(r, k) => zetInGripp(r, 'live', k)} onKloptNiet={(r) => kloptNiet(r, 'live')} onTrekIn={(a) => trekIn(a, 'live')} />
      ) : <CircularProgress size={18} sx={{ color: '#adb3b5' }} />}

      <Kop sub="Eenmalig: wat had de PO-check de afgelopen maanden gevonden? Alleen lezen.">Terugkijkanalyse</Kop>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
        {terugStatus?.bezig ? (
          <Typography sx={{ fontSize: '0.84rem', color: GRIJS }}>
            Bezig… {terugStatus.gelezen || 0} mails gelezen, {terugStatus.met_po || 0} met PO.
          </Typography>
        ) : (
          <>
            {[3, 6, 12].map((m) => (
              <Button key={m} size="small" onClick={() => startTerug(m)} sx={{ textTransform: 'none' }}>{m} maanden</Button>
            ))}
          </>
        )}
        {res && (
          <Button size="small" onClick={() => setTerugOpen((x) => !x)} sx={{ textTransform: 'none', color: GRIJS, ml: 'auto' }}>
            {terugOpen ? 'Resultaat verbergen' : 'Resultaat tonen'}
          </Button>
        )}
      </Box>
      {res && (
        <Typography sx={{ fontSize: '0.84rem', mt: 1 }}>
          {datum(terug.klaar)} · {res.periode?.maanden} maanden · {res.totalen?.mails} mails, {res.totalen?.met_po} met PO ·
          {' '}{res.totalen?.eenduidig} eenduidig, {res.totalen?.twijfel} twijfel, {res.totalen?.geen_match} geen match ·
          {' '}{res.zou_invullen} zou de tool invullen
          {!res.volledig && <Typography component="span" sx={{ color: '#92400e', fontSize: '0.8rem' }}> · afgekapt: {(res.afgekapt || []).join('; ')}</Typography>}
        </Typography>
      )}
      {res?.kalibratie && (
        <Typography sx={{ fontSize: '0.8rem', color: GRIJS, mt: 0.5 }}>
          Controle tegen Gripp (waar de PO al staat): {res.kalibratie.met_historie_op_project} PO's staan al op een project ·
          {' '}de tool had er {res.kalibratie.tool_correct} goed, {res.kalibratie.tool_onterecht} fout en {res.kalibratie.niet_gekoppeld} niet
          automatisch · juiste project in het voorstel: {res.kalibratie.juiste_project_in_voorstel}
        </Typography>
      )}
      {res && terugOpen && (
        <Box sx={{ mt: 1.5 }}>
          <PoRegels regels={res.regels || []} mag={mag} bron="terugkijk"
            onZet={(r, k) => zetInGripp(r, 'terugkijk', k)} onKloptNiet={(r) => kloptNiet(r, 'terugkijk')}
            onTrekIn={(a) => trekIn(a, 'terugkijk')} />
        </Box>
      )}
    </Box>
  );
}
